import { supabase } from '../../../shared/lib/supabaseClient';
import { getDateRange, getTrendPeriods } from '../../../shared/utils/reportingPeriod';

const DISBURSED_STATUSES = ['Ongoing', 'Completed'];
const CACHE_TTL_MS = 60 * 1000;

const pad = (n) => String(n).padStart(2, '0');

async function getTotalDisbursed(periods) {
  let query = supabase.from('projects').select('amount').in('status', DISBURSED_STATUSES);

  const range = getDateRange(periods);
  if (range) query = query.gte('expense_date', range.start).lte('expense_date', range.end);

  const { data, error } = await query;
  if (error) throw error;
  return data.reduce((sum, row) => sum + Number(row.amount), 0);
}

// Disbursed amount per calendar month, keyed "YYYY-MM".
async function getDisbursedByMonth(periods) {
  const range = getDateRange(periods);
  const { data, error } = await supabase
    .from('projects')
    .select('amount, expense_date')
    .in('status', DISBURSED_STATUSES)
    .gte('expense_date', range.start)
    .lte('expense_date', range.end);
  if (error) throw error;

  const byMonth = {};
  data.forEach((row) => {
    if (!row.expense_date) return;
    const key = row.expense_date.slice(0, 7);
    byMonth[key] = (byMonth[key] ?? 0) + Number(row.amount);
  });
  return byMonth;
}

// The stats and trend requests overlap heavily, so share identical calls for a short while.
const statsCache = new Map();

function getPaymentStats({ month, year }, section) {
  const key = `${month}|${year}|${section ?? ''}`;
  const cached = statsCache.get(key);
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) return cached.promise;

  const promise = supabase
    .rpc('get_public_payment_stats', {
      p_month: month ?? null,
      p_year: year ?? null,
      p_section: section ?? null,
    })
    .then(({ data, error }) => {
      if (error) throw error;
      return data;
    });

  promise.catch(() => statsCache.delete(key));
  statsCache.set(key, { promise, at: Date.now() });
  return promise;
}

const sumInto = (target, source = {}) => {
  Object.entries(source).forEach(([key, value]) => {
    target[key] = (target[key] ?? 0) + Number(value);
  });
};

// Enrolment does not add up across months, so keep the highest count seen for each grade.
const maxInto = (target, source = {}) => {
  Object.entries(source).forEach(([key, value]) => {
    target[key] = Math.max(target[key] ?? 0, Number(value));
  });
};

export async function getPublicStats(periods, section) {
  const [results, totalDisbursed] = await Promise.all([
    Promise.all(periods.map((period) => getPaymentStats(period, section))),
    getTotalDisbursed(periods),
  ]);

  const collectedByGrade = {};
  const collectedByCategory = {};
  const studentCountByGrade = {};
  const sections = new Set();
  let totalCollected = 0;

  results.forEach((data) => {
    totalCollected += Number(data?.totalCollected ?? 0);
    sumInto(collectedByGrade, data?.collectedByGrade);
    sumInto(collectedByCategory, data?.collectedByCategory);
    maxInto(studentCountByGrade, data?.studentCountByGrade);
    (data?.sections ?? []).forEach((s) => sections.add(s));
  });

  // Distinct payers cannot be added across months, so only a single period reports them.
  // `paidStudentCount` is optional: the stats function has to return it for this to show.
  const paidStudentCount =
    results.length === 1 && results[0]?.paidStudentCount != null
      ? Number(results[0].paidStudentCount)
      : null;

  return {
    totalCollected,
    collectedByGrade,
    collectedByCategory,
    studentCountByGrade,
    sections: [...sections],
    totalDisbursed,
    paidStudentCount,
  };
}

// Month-by-month totals for one school year (plus the month before it), for trends.
export async function getMonthlyTrend(schoolYear) {
  const periods = getTrendPeriods(schoolYear);
  if (periods.length === 0) return [];

  const [results, disbursedByMonth] = await Promise.all([
    Promise.all(periods.map((period) => getPaymentStats(period, null))),
    getDisbursedByMonth(periods),
  ]);

  return periods.map(({ month, year }, i) => ({
    month,
    year,
    collected: Number(results[i]?.totalCollected ?? 0),
    disbursed: disbursedByMonth[`${year}-${pad(month)}`] ?? 0,
  }));
}
